import type { FunnelEvent } from "./types";

// The widget never touches GA/Ads directly. It posts structured messages to the
// parent page; embed.js relays them into the parent's dataLayer/GTM. This keeps
// analytics first-party (the parent's domain context) and fully decoupled from
// whatever conversion labels Bond's marketing configures in GTM.

let targetOrigin = "*";

export function initAnalytics(parentOrigin: string): void {
  targetOrigin = parentOrigin || "*";
}

function send(msg: Record<string, unknown>): void {
  try {
    window.parent.postMessage({ source: "bbg-widget", ...msg }, targetOrigin);
  } catch {
    /* parent may be gone; analytics is best-effort */
  }
}

/** Funnel step event -> dataLayer event `bbg_<event>`. */
export function track(event: FunnelEvent, props: Record<string, unknown> = {}): void {
  send({ kind: "analytics", event, props });
}

/** Tell the parent loader how tall the iframe content is, so it can resize. */
export function reportHeight(height: number): void {
  send({ kind: "resize", height: Math.ceil(height) });
}

/** Give the parent loader the brand facts it needs to advertise the booking
 *  capability (JSON-LD ReserveAction) on the practice page. Data only — the
 *  parent decides whether/how to inject (see embed.js, data-bbg-schema). */
export function announceBrand(payload: { name: string; phone: string; phoneHref: string; consultIsFree: boolean }): void {
  send({ kind: "brand", ...payload });
}

/** Step changed: reset our own scroll and ask the parent to bring the widget
 *  top back into view (only acted on when it scrolled out — see embed.js). */
export function scrollToTop(): void {
  try {
    window.scrollTo({ top: 0 });
  } catch {
    /* older browsers */
  }
  send({ kind: "scrolltop" });
}

/** Booking success -> parent fires the Google Ads / GA4 conversion. */
export function conversion(payload: {
  value: number;
  currency: string;
  requestId?: string;
  locationId: string;
  appointmentTime?: string;
  gclid?: string;
}): void {
  send({ kind: "conversion", ...payload });
}

/** Observe the document height and stream it to the parent for auto-resize. */
export function observeHeight(el: HTMLElement): () => void {
  const emit = () => reportHeight(el.getBoundingClientRect().height);
  emit();
  const ro = new ResizeObserver(emit);
  ro.observe(el);
  window.addEventListener("load", emit);
  return () => ro.disconnect();
}
